import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button, Card, CardBody, Spinner, Typography } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import {
    MPESA_WALLET_COPY,
    applyMpesaFormStateChange,
    getMpesaIdentityFieldValue,
    getMpesaLinkedBusinessChild,
    getMpesaWalletPresentation,
    isLockedMpesaParent,
    isValidBusinessParent,
} from './mpesaWalletHelpers';

const { rootUrl } = require('../../../config/config');

const defaultFormData = {
    account_name: '',
    account_type: 'BANK',
    gl_account_code: '',
    bank_account_number: '',
    bank_name: '',
    mpesa_phone: '',
    mpesa_role: 'PERSONAL',
    mpesa_parent_account_id: '',
    has_pochi: false,
    opening_balance: 0,
    business_opening_balance: 0,
    is_active: true,
    input_by: '',
};

const accountTypeOptions = [
    { value: 'CASH', label: 'Cash' },
    { value: 'MPESA', label: 'M-Pesa' },
    { value: 'BANK', label: 'Bank' },
    { value: 'INCOME', label: 'Income' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'SAVINGS', label: 'Savings' },
    { value: 'LIABILITY', label: 'Liability' },
];

const mpesaRoleOptions = [
    { value: 'PERSONAL', label: 'PERSONAL' },
    { value: 'BUSINESS', label: 'BUSINESS' },
];

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '46px',
        borderRadius: '0.75rem',
        borderColor: state.isFocused ? '#FF5733' : '#cbd5e1',
        boxShadow: 'none',
        fontSize: '0.95rem',
        '&:hover': {
            borderColor: '#FF5733',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 0.85rem',
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
    }),
    singleValue: (base) => ({
        ...base,
        color: '#334155',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
    }),
    indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: '#e2e8f0',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '0.75rem',
        overflow: 'hidden',
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? '#fff1eb' : '#ffffff',
        color: '#334155',
        cursor: 'pointer',
    }),
};

const buildFormData = (user, account = null, accounts = []) => {
    const linkedBusinessChild = account ? getMpesaLinkedBusinessChild(account, accounts) : null;

    return {
        ...defaultFormData,
        account_name: account?.account_name || defaultFormData.account_name,
        account_type: account?.account_type || defaultFormData.account_type,
        gl_account_code: account?.gl_account_code || defaultFormData.gl_account_code,
        bank_account_number: account?.bank_account_number || defaultFormData.bank_account_number,
        bank_name: account?.bank_name || defaultFormData.bank_name,
        mpesa_phone: account?.mpesa_phone || defaultFormData.mpesa_phone,
        mpesa_role: account?.mpesa_role || defaultFormData.mpesa_role,
        mpesa_parent_account_id: account?.mpesa_parent_account_id ? String(account.mpesa_parent_account_id) : defaultFormData.mpesa_parent_account_id,
        has_pochi: account ? Boolean(account.has_pochi) : defaultFormData.has_pochi,
        opening_balance: account ? Number(account.opening_balance) || 0 : defaultFormData.opening_balance,
        business_opening_balance: linkedBusinessChild ? Number(linkedBusinessChild.opening_balance) || 0 : defaultFormData.business_opening_balance,
        is_active: account ? account.is_active !== false : defaultFormData.is_active,
        input_by: user?.username || defaultFormData.input_by,
    };
};

const cashbookGlAccountTypes = ['BANK', 'CASH', 'MPESA', 'SAVINGS', 'LIABILITY'];

const CashbookAccountFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';
    const isEditing = Boolean(id && id !== 'new');

    const [accounts, setAccounts] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingGlAccounts, setLoadingGlAccounts] = useState(false);
    const [formError, setFormError] = useState('');
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState(() => buildFormData(user));

    const accountLookup = useMemo(() => {
        return accounts.reduce((lookup, account) => {
            lookup[String(account.id)] = account;
            return lookup;
        }, {});
    }, [accounts]);

    const currentAccount = useMemo(() => {
        if (!isEditing) return null;
        return accounts.find((account) => String(account.id) === String(id)) || null;
    }, [accounts, id, isEditing]);

    const lockedMpesaParent = isLockedMpesaParent(currentAccount, accounts);
    const mpesaParentOptions = useMemo(() => {
        return accounts
            .filter((account) => isValidBusinessParent(account, formData.mpesa_phone?.trim() || ''))
            .map((account) => ({
                value: String(account.id),
                label: getMpesaWalletPresentation(account, accountLookup, accounts).optionLabel,
                account,
            }));
    }, [accounts, accountLookup, formData.mpesa_phone]);

    const glAccountOptions = useMemo(() => {
        return glAccounts.map((account) => ({
            value: account.account_code,
            label: `${account.account_code} - ${account.account_name} (${account.account_type})`,
        }));
    }, [glAccounts]);

    const selectedGlAccount = glAccountOptions.find((option) => option.value === formData.gl_account_code) || null;

    const updateMpesaFormData = useCallback((updates) => {
        setFormData((prev) => applyMpesaFormStateChange(prev, updates, accounts));
    }, [accounts]);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (response.data?.success) {
                setAccounts(response.data.data || []);
            } else {
                setAccounts(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    const fetchGlAccounts = useCallback(async () => {
        setLoadingGlAccounts(true);
        try {
            const response = await axios.get(`${rootUrl}/system/coa/accounts`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            const coaData = response.data?.data || [];
            setGlAccounts(coaData.filter((account) => account.is_enabled !== false));
        } catch (error) {
            console.error('Error fetching GL accounts:', error);
            setGlAccounts([]);
        } finally {
            setLoadingGlAccounts(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchAccounts();
        fetchGlAccounts();
    }, [fetchAccounts, fetchGlAccounts]);

    useEffect(() => {
        setFormData(buildFormData({ username: user?.username || '' }, currentAccount, accounts));
    }, [currentAccount, accounts, user?.username]);

    const handleAccountTypeChange = (option) => {
        setFormData((prev) => ({
            ...prev,
            account_type: option?.value || '',
        }));
    };

    const handleMpesaRoleChange = (option) => {
        updateMpesaFormData({
            mpesa_role: option?.value || 'PERSONAL',
        });
    };

    const handleMpesaParentChange = (option) => {
        updateMpesaFormData({
            mpesa_parent_account_id: option?.value || '',
        });
    };

    const validate = () => {
        const newErrors = {};
        const mpesaPhone = formData.mpesa_phone?.trim() || '';

        if (!formData.account_name?.trim()) newErrors.account_name = 'Account name is required';
        if (!formData.account_type) newErrors.account_type = 'Account type is required';
        if (!['INCOME', 'EXPENSE', 'BANK', 'CASH', 'MPESA', 'SAVINGS', 'LIABILITY'].includes(formData.account_type)) {
            newErrors.account_type = 'Invalid account type';
        }
        if (cashbookGlAccountTypes.includes(formData.account_type) && !formData.gl_account_code) {
            newErrors.gl_account_code = 'Cashbook GL account is required';
        }
        if (Number.isNaN(Number(formData.opening_balance))) newErrors.opening_balance = 'Opening balance must be a valid number';

        if (lockedMpesaParent) {
            const lockedIdentityFields = ['account_type', 'mpesa_role', 'mpesa_parent_account_id', 'mpesa_phone', 'has_pochi'];
            const identityChanged = lockedIdentityFields.some((field) => getMpesaIdentityFieldValue(formData, field) !== getMpesaIdentityFieldValue(currentAccount, field));

            if (identityChanged) {
                lockedIdentityFields.forEach((field) => {
                    newErrors[field] = 'This linked PERSONAL wallet has BUSINESS children and its wallet identity cannot be changed.';
                });
            }
        }

        if (formData.account_type === 'MPESA' && !mpesaPhone) {
            newErrors.mpesa_phone = 'M-Pesa phone number is required';
        } else if (mpesaPhone && !/^\d{10,15}$/.test(mpesaPhone)) {
            newErrors.mpesa_phone = 'Phone must be 10–15 digits';
        }

        if (formData.account_type === 'MPESA') {
            if (!['PERSONAL', 'BUSINESS'].includes(formData.mpesa_role)) {
                newErrors.mpesa_role = 'Select a valid M-Pesa wallet role';
            }

            if (formData.mpesa_role === 'BUSINESS') {
                if (!formData.mpesa_parent_account_id) {
                    newErrors.mpesa_parent_account_id = 'Select the linked personal wallet';
                } else {
                    const parentAccount = accounts.find((account) => String(account.id) === String(formData.mpesa_parent_account_id));

                    if (!parentAccount) {
                        newErrors.mpesa_parent_account_id = 'Selected personal wallet was not found';
                    } else if (!isValidBusinessParent(parentAccount, mpesaPhone)) {
                        newErrors.mpesa_parent_account_id = 'Parent wallet must be a PERSONAL M-Pesa account';
                    } else if (currentAccount && String(parentAccount.id) === String(currentAccount.id)) {
                        newErrors.mpesa_parent_account_id = 'An account cannot be its own parent';
                    }
                }
            }

            if (formData.mpesa_role !== 'BUSINESS' && formData.mpesa_parent_account_id) {
                newErrors.mpesa_parent_account_id = 'Only BUSINESS wallet rows can have a parent wallet';
            }

            if (formData.mpesa_role === 'BUSINESS' && formData.has_pochi) {
                newErrors.has_pochi = 'BUSINESS wallet rows cannot enable Pochi';
            }

            if (formData.mpesa_role === 'PERSONAL' && formData.has_pochi && Number.isNaN(Number(formData.business_opening_balance))) {
                newErrors.business_opening_balance = 'Linked business opening balance must be a valid number';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setSaving(true);
        setFormError('');
        setErrors({});

        const payload = {
            ...formData,
            account_name: formData.account_name.trim(),
            gl_account_code: formData.gl_account_code?.trim() || null,
            bank_name: formData.bank_name?.trim() || '',
            bank_account_number: formData.bank_account_number?.trim() || '',
            mpesa_phone: formData.account_type === 'MPESA' ? formData.mpesa_phone?.trim() || null : null,
            mpesa_role: formData.account_type === 'MPESA' ? formData.mpesa_role || 'PERSONAL' : 'PERSONAL',
            mpesa_parent_account_id: formData.account_type === 'MPESA' && formData.mpesa_role === 'BUSINESS' ? formData.mpesa_parent_account_id || null : null,
            has_pochi: formData.account_type === 'MPESA' ? Boolean(formData.has_pochi) : false,
            opening_balance: Number.parseFloat(formData.opening_balance) || 0,
            business_opening_balance: formData.account_type === 'MPESA' && formData.mpesa_role === 'PERSONAL' && formData.has_pochi
                ? Number.parseFloat(formData.business_opening_balance) || 0
                : 0,
        };

        try {
            const request = isEditing
                ? axios.patch(
                    `${rootUrl}/api/cashbook/accounts/${id}`,
                    {
                        ...payload,
                        edit_by: user?.username || 'system',
                    },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                )
                : axios.post(
                    `${rootUrl}/api/cashbook/accounts`,
                    {
                        ...payload,
                        input_by: user?.username || 'system',
                    },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );

            const response = await request;
            if (!response.data?.success) {
                const apiError = new Error(response.data?.message || 'Failed to save account');
                apiError.apiErrors = response.data?.errors || null;
                throw apiError;
            }

            navigate('/cashbook/accounts');
        } catch (err) {
            console.error('Error saving account:', err);
            const apiErrors = err.response?.data?.errors || err.apiErrors || null;
            const apiMessage = err.response?.data?.message || err.message || 'Failed to save account';

            if (apiErrors && typeof apiErrors === 'object') {
                setErrors(apiErrors);
                setFormError(apiMessage === 'Failed to save account' ? 'Please correct the highlighted fields and try again.' : apiMessage);
            } else {
                setFormError(apiMessage);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/cashbook/accounts');
    };

    if (loading && (!isEditing || !currentAccount)) {
        return (
            <div className="min-h-screen bg-[#fff8f5] flex items-center justify-center">
                <div className="text-center">
                    <Spinner className="h-8 w-8 text-[#FF5733] mx-auto mb-3" />
                    <Typography className="text-sm text-slate-600">Loading cashbook account form...</Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="h-9 w-9 flex items-center justify-center rounded-sm border border-slate-300 hover:bg-white text-slate-600 transition-colors flex-shrink-0"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
                    </button>
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF5733]">Cashbook</p>
                        <h1 className="text-xl font-bold text-gray-900 leading-tight">
                            {isEditing ? 'Edit Cashbook Account' : 'New Cashbook Account'}
                        </h1>
                        <p className="text-sm text-slate-600 mt-1">Set the cashbook details and map the account to its GL balance sheet account.</p>
                    </div>
                </div>
            </div>

            <div className="w-full min-w-0 px-3 sm:px-4 lg:px-6 pb-8">
                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-4 sm:p-5">
                        {formError && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-5">
                                <Typography className="text-sm font-medium text-red-700">{formError}</Typography>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div>
                                    <Typography className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">General</Typography>
                                    <Typography className="text-sm text-slate-500 mt-1">Basic identity, account type, and GL mapping.</Typography>
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5 sm:col-span-2">
                                        <Typography className="text-sm font-semibold text-slate-700">Account Name</Typography>
                                        <input
                                            aria-label="Account Name"
                                            type="text"
                                            value={formData.account_name}
                                            onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                                            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all ${errors.account_name ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'}`}
                                        />
                                        {errors.account_name && <Typography className="mt-1 text-xs text-red-500">{errors.account_name}</Typography>}
                                    </div>

                                    <div className="space-y-1.5 sm:col-span-2">
                                        <Typography className="text-sm font-semibold text-slate-700">Account Type</Typography>
                                        <Select
                                            inputId="account_type"
                                            value={accountTypeOptions.find((option) => option.value === formData.account_type)}
                                            onChange={handleAccountTypeChange}
                                            options={accountTypeOptions}
                                            styles={selectStyles}
                                            isSearchable={false}
                                            isDisabled={lockedMpesaParent}
                                        />
                                        {errors.account_type && <Typography className="mt-1 text-xs text-red-500">{errors.account_type}</Typography>}
                                    </div>

                                    <div className="space-y-1.5 sm:col-span-2">
                                        <Typography className="text-sm font-semibold text-slate-700">Cashbook GL Account</Typography>
                                        <Select
                                            inputId="gl_account_code"
                                            options={glAccountOptions}
                                            value={selectedGlAccount}
                                            onChange={(option) => setFormData((prev) => ({ ...prev, gl_account_code: option?.value || '' }))}
                                            styles={selectStyles}
                                            isSearchable
                                            isClearable
                                            placeholder={loadingGlAccounts ? 'Loading GL accounts...' : 'Select GL account'}
                                            isDisabled={loadingGlAccounts}
                                        />
                                        <Typography className="text-xs text-slate-500">Use the balance-sheet GL account that represents this cashbook account.</Typography>
                                        {errors.gl_account_code && <Typography className="mt-1 text-xs text-red-500">{errors.gl_account_code}</Typography>}
                                    </div>
                                </div>
                            </section>

                            {formData.account_type === 'MPESA' ? (
                                <section className="space-y-4 rounded-2xl border border-amber-100 bg-amber-50/40 p-4 shadow-sm">
                                    <div>
                                        <Typography className="text-xs font-black uppercase tracking-[0.2em] text-amber-600">M-Pesa Wallet Setup</Typography>
                                        <Typography className="text-sm text-amber-900/80 mt-1">Configure the phone, role, and linked Pochi wallet.</Typography>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="space-y-1.5 sm:col-span-2">
                                            <Typography className="text-sm font-semibold text-slate-700">M-Pesa Phone Number</Typography>
                                            <input
                                                type="text"
                                                value={formData.mpesa_phone}
                                                onChange={(e) => updateMpesaFormData({ mpesa_phone: e.target.value })}
                                                placeholder="e.g. 0712345678"
                                                disabled={lockedMpesaParent}
                                                className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all ${errors.mpesa_phone ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'} ${lockedMpesaParent ? 'cursor-not-allowed bg-slate-100' : ''}`}
                                            />
                                            {errors.mpesa_phone && <Typography className="mt-1 text-xs text-red-500">{errors.mpesa_phone}</Typography>}
                                        </div>

                                        <div className="space-y-1.5 sm:col-span-2">
                                            <Typography className="text-sm font-semibold text-slate-700">Wallet Role</Typography>
                                            <Select
                                                value={mpesaRoleOptions.find((option) => option.value === formData.mpesa_role)}
                                                onChange={handleMpesaRoleChange}
                                                options={mpesaRoleOptions}
                                                styles={selectStyles}
                                                isSearchable={false}
                                                isDisabled={lockedMpesaParent}
                                            />
                                            {errors.mpesa_role && <Typography className="mt-1 text-xs text-red-500">{errors.mpesa_role}</Typography>}
                                        </div>

                                        {formData.mpesa_role === 'PERSONAL' && (
                                            <div className="space-y-2 rounded-xl border border-emerald-100 bg-white px-4 py-3 sm:col-span-2">
                                                <label className="flex items-start gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.has_pochi}
                                                        onChange={(e) => updateMpesaFormData({ has_pochi: e.target.checked })}
                                                        disabled={lockedMpesaParent}
                                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-[#FF5733] focus:ring-[#FF5733]"
                                                    />
                                                    <span className="text-sm text-slate-700">
                                                        <span className="font-semibold">Has Pochi / Business wallet</span>
                                                        <span className="block text-xs text-slate-500 mt-1">{MPESA_WALLET_COPY.personalPochiToggle}</span>
                                                    </span>
                                                </label>
                                                {errors.has_pochi && <Typography className="text-xs text-red-500">{errors.has_pochi}</Typography>}
                                            </div>
                                        )}

                                        {formData.mpesa_role === 'PERSONAL' && formData.has_pochi && (
                                            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 sm:col-span-2">
                                                <Typography className="text-sm font-semibold text-blue-700">Linked business wallet ready</Typography>
                                                <Typography className="text-xs text-blue-700 mt-1">{MPESA_WALLET_COPY.personalReady}</Typography>
                                            </div>
                                        )}

                                        {formData.mpesa_role === 'BUSINESS' && (
                                            <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:col-span-2">
                                                <Typography className="text-sm font-semibold text-slate-700">Linked PERSONAL Wallet</Typography>
                                                <Select
                                                    value={mpesaParentOptions.find((option) => option.value === formData.mpesa_parent_account_id) || null}
                                                    onChange={handleMpesaParentChange}
                                                    options={mpesaParentOptions}
                                                    styles={selectStyles}
                                                    isSearchable={false}
                                                    placeholder={mpesaParentOptions.length > 0 ? 'Select the personal wallet' : 'No PERSONAL wallet available'}
                                                    isDisabled={mpesaParentOptions.length === 0}
                                                />
                                                <Typography className="text-xs text-slate-500">{MPESA_WALLET_COPY.businessParent}</Typography>
                                                {errors.mpesa_parent_account_id && <Typography className="mt-1 text-xs text-red-500">{errors.mpesa_parent_account_id}</Typography>}
                                            </div>
                                        )}

                                        {lockedMpesaParent && (
                                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:col-span-2">
                                                <Typography className="text-sm font-semibold text-amber-800">Wallet identity locked</Typography>
                                                <Typography className="text-xs text-amber-800 mt-1">{MPESA_WALLET_COPY.lockedWallet}</Typography>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ) : (
                                <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                    <div>
                                        <Typography className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Bank Details</Typography>
                                        <Typography className="text-sm text-slate-500 mt-1">Only shown for non-M-Pesa accounts.</Typography>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="space-y-1.5">
                                            <Typography className="text-sm font-semibold text-slate-700">Bank Name</Typography>
                                            <input
                                                type="text"
                                                value={formData.bank_name}
                                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733]"
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <Typography className="text-sm font-semibold text-slate-700">Bank Account Number</Typography>
                                            <input
                                                type="text"
                                                value={formData.bank_account_number}
                                                onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733]"
                                            />
                                        </div>
                                    </div>
                                </section>
                            )}

                            <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div>
                                    <Typography className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Opening Balances</Typography>
                                    <Typography className="text-sm text-slate-500 mt-1">Set the starting balance for the main wallet and, when enabled, the linked business wallet.</Typography>
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Typography className="text-sm font-semibold text-slate-700">Primary Opening Balance</Typography>
                                        <input
                                            type="number"
                                            value={formData.opening_balance}
                                            onChange={(e) => updateMpesaFormData({ opening_balance: e.target.value })}
                                            step="0.01"
                                            className={`w-full rounded-xl border bg-white px-4 py-3 text-right text-sm text-slate-700 outline-none transition-all ${errors.opening_balance ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'}`}
                                        />
                                        {errors.opening_balance && <Typography className="mt-1 text-xs text-red-500">{errors.opening_balance}</Typography>}
                                    </div>

                                    {formData.account_type === 'MPESA' && formData.mpesa_role === 'PERSONAL' && formData.has_pochi ? (
                                        <div className="space-y-1.5">
                                            <Typography className="text-sm font-semibold text-slate-700">Linked Business Opening Balance</Typography>
                                            <input
                                                type="number"
                                                value={formData.business_opening_balance}
                                                onChange={(e) => updateMpesaFormData({ business_opening_balance: e.target.value })}
                                                step="0.01"
                                                className={`w-full rounded-xl border bg-white px-4 py-3 text-right text-sm text-slate-700 outline-none transition-all ${errors.business_opening_balance ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'}`}
                                            />
                                            <Typography className="text-xs text-slate-500">{MPESA_WALLET_COPY.businessOpeningBalance}</Typography>
                                            {errors.business_opening_balance && <Typography className="text-xs text-red-500">{errors.business_opening_balance}</Typography>}
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
                                            <Typography className="text-sm font-semibold text-slate-500">Linked Business Opening Balance</Typography>
                                            <Typography className="text-xs text-slate-500 mt-1">Shows only when a PERSONAL M-Pesa wallet has Pochi enabled.</Typography>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 bg-white shadow-sm">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="h-4 w-4 rounded border-slate-300 text-[#FF5733] focus:ring-[#FF5733]"
                                />
                                <span className="text-sm font-medium text-slate-700">Account is active</span>
                            </label>

                            <div className="flex flex-wrap gap-2 justify-end pt-2">
                                <Button
                                    type="button"
                                    onClick={handleCancel}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faSyncAlt} className={saving ? 'animate-spin' : ''} />
                                    {saving ? 'Saving...' : isEditing ? 'Update Account' : 'Create Account'}
                                </Button>
                            </div>
                        </form>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default CashbookAccountFormPage;
